// ===== Production Incoming Call Dialog Component =====

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Avatar
} from '@mui/material';
import {
  Call,
  CallEnd,
  VideoCall
} from '@mui/icons-material';
import { sanitizeInput } from '../utils/ValidationUtils';

const IncomingCallDialog = ({ incomingCall, onAccept, onReject }) => {
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    if (incomingCall) {
      setShowAnimation(true);
      // Add ring sound if available
      if (window.Audio) {
        try {
          const audio = new Audio('/sounds/ringtone.mp3');
          audio.loop = true;
          audio.play().catch(e => console.log('Could not play ringtone:', e));
          
          return () => {
            audio.pause();
            audio.currentTime = 0;
          };
        } catch (error) {
          console.warn('Ringtone not available:', error);
        }
      }
    }
  }, [incomingCall]);

  if (!incomingCall) return null;

  const callerName = sanitizeInput(incomingCall.callerName || 'Unknown Caller');
  const isVideoCall = incomingCall.callType === 'video';

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
          
          .incoming-call-animation {
            animation: pulse 2s infinite ease-in-out;
          }
          
          .gradient-background {
            background: linear-gradient(135deg, #128C7E 0%, #075E54 100%);
          }
        `}
      </style>

      <Dialog
        open={true}
        fullScreen
        PaperProps={{
          style: {
            background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
            color: 'white'
          }
        }}
      >
        <DialogContent
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100vh',
            padding: '60px 40px 40px',
            textAlign: 'center'
          }}
        >
          {/* Call Type Indicator */}
          <Box style={{ marginBottom: '20px' }}>
            <Typography variant="h6" style={{ opacity: 0.9 }}>
              Incoming {isVideoCall ? 'Video' : 'Voice'} Call
            </Typography>
          </Box>

          {/* Caller Information */}
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flex: 1,
              justifyContent: 'center'
            }}
          >
            {/* Caller Avatar */}
            <Box
              className={showAnimation ? 'incoming-call-animation' : ''}
              style={{
                marginBottom: '30px'
              }}
            >
              <Avatar
                style={{
                  width: '150px',
                  height: '150px',
                  backgroundColor: '#25D366',
                  fontSize: '48px',
                  fontWeight: 'bold',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                  border: '4px solid rgba(255,255,255,0.2)'
                }}
              >
                {callerName.charAt(0).toUpperCase()}
              </Avatar>
            </Box>

            {/* Caller Name */}
            <Typography
              variant="h3"
              style={{
                fontWeight: '300',
                marginBottom: '10px',
                wordBreak: 'break-word',
                maxWidth: '90%'
              }}
            >
              {callerName}
            </Typography>

            {/* Call Status */}
            <Typography
              variant="h6"
              style={{
                opacity: 0.8,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isVideoCall ? '📹' : '📞'}
              {isVideoCall ? 'Video calling...' : 'Voice calling...'}
            </Typography>

            {/* Additional Call Info */}
            <Typography
              variant="body2"
              style={{
                opacity: 0.6,
                marginTop: '16px'
              }}
            >
              Encrypted
            </Typography>
          </Box>

          {/* Call Action Buttons */}
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              width: '100%',
              maxWidth: '300px',
              marginTop: '60px'
            }}
          >
            {/* Reject Button */}
            <IconButton
              onClick={onReject}
              style={{
                width: '70px',
                height: '70px',
                backgroundColor: '#ff4444',
                color: 'white',
                boxShadow: '0 8px 20px rgba(255,68,68,0.4)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
              aria-label="Reject call"
            >
              <CallEnd style={{ fontSize: '32px' }} />
            </IconButton>

            {/* Accept Button */}
            <IconButton
              onClick={onAccept}
              style={{
                width: '70px',
                height: '70px',
                backgroundColor: '#25D366',
                color: 'white',
                boxShadow: '0 8px 20px rgba(37,211,102,0.4)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
              aria-label={`Accept ${isVideoCall ? 'video' : 'voice'} call`}
            >
              {isVideoCall ? (
                <VideoCall style={{ fontSize: '32px' }} />
              ) : (
                <Call style={{ fontSize: '32px' }} />
              )}
            </IconButton>
          </Box>

          {/* Quick Actions */}
          <Box
            style={{
              display: 'flex',
              gap: '16px',
              marginTop: '20px',
              opacity: 0.7
            }}
          >
            <Typography variant="caption">
              Swipe up for more options
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default React.memo(IncomingCallDialog);
